/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 * Routes: / = landing, /app = editor.
 */

import { StrictMode, useState, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { App } from "./App";
import { LandingPage } from "./LandingPage";
import { Wizard } from "./components/Wizard/Wizard";
import { Dashboard } from "./Dashboard";
import { Pricing } from "./Pricing";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppProvider } from "./contexts/AppContext";
import { I18nProvider, getT, useI18n } from "./contexts/I18nContext";
import { SettingsProvider, useSettings } from "./contexts/SettingsContext";
import { ASSET_BASE } from "./constants";
import { LoginModal } from "./components/LoginModal";
import { ResetPasswordPage } from "./components/ResetPasswordPage";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Favicon / app icons (injected at runtime to avoid bundler resolving hrefs in HTML)
if (typeof document !== "undefined") {
  const base = ASSET_BASE === "" ? "" : ASSET_BASE.replace(/\/$/, "");
  const p = (path: string) => (base ? `${base}/${path}` : `/${path}`);
  const links = [
    { rel: "icon", type: "image/webp", sizes: "32x32", href: p("favicon-32.webp") },
    { rel: "icon", type: "image/webp", sizes: "16x16", href: p("favicon-16.webp") },
    { rel: "apple-touch-icon", href: p("apple-touch-icon.webp") },
    { rel: "manifest", href: p("site.webmanifest") },
  ];
  for (const attrs of links) {
    if (document.head.querySelector(`link[rel="${attrs.rel}"]`)) continue;
    const link = document.createElement("link");
    Object.assign(link, attrs);
    document.head.appendChild(link);
  }
}

// eslint-disable-next-line react-refresh/only-export-components
function AppWrapper({ children }: { children: React.ReactNode }) {
  const { settings } = useSettings();
  const locale = settings.locale;
  const t = useMemo(() => getT(locale), [locale]);
  return (
    <I18nProvider locale={locale} t={t}>
      {children}
    </I18nProvider>
  );
}

// Auth wall shown when user tries to access a protected route without being logged in
// eslint-disable-next-line react-refresh/only-export-components
function AuthWall() {
  const { signIn, signUp, resetPasswordForEmail } = useAuth();
  const { t } = useI18n();
  const [showLogin, setShowLogin] = useState(false);
  const [_loginMode, setLoginMode] = useState<'login' | 'signup'>('signup');

  return (
    <div className="min-h-screen bg-[#0e0e1a] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-black text-white">{t('auth.required')}</h1>
          <p className="text-sm text-white/50">{t('auth.requiredDesc')}</p>
        </div>
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => { setLoginMode('signup'); setShowLogin(true); }}
            className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-bold text-sm transition-colors"
          >
            {t('auth.signupFree')}
          </button>
          <button
            type="button"
            onClick={() => { setLoginMode('login'); setShowLogin(true); }}
            className="w-full py-3 rounded-xl border border-white/10 text-white/60 hover:text-white hover:bg-white/5 font-medium text-sm transition-colors"
          >
            {t('auth.alreadyAccount')}
          </button>
        </div>
      </div>
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onLogin={async (email, password) => { const { error } = await signIn(email, password); if (error) throw error; }}
        onSignup={async (email, password) => { const { error } = await signUp(email, password); if (error) throw error; }}
        onForgotPassword={async (email) => { const { error } = await resetPasswordForEmail(email); if (error) throw error; }}
      />
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0e0e1a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <AuthWall />;
  return <>{children}</>;
}

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <ErrorBoundary>
      <AppProvider>
        <AuthProvider>
          {/* SettingsProvider est à l'intérieur de AuthProvider pour pouvoir utiliser useAuth() */}
          <SettingsProvider>
            <AppWrapper>
              <BrowserRouter>
                <Routes>
                  <Route path="/app" element={<ProtectedRoute><ErrorBoundary><App /></ErrorBoundary></ProtectedRoute>} />
                  <Route path="/wizard" element={<ProtectedRoute><Wizard /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/" element={<LandingPage />} />
                </Routes>
              </BrowserRouter>
            </AppWrapper>
          </SettingsProvider>
        </AuthProvider>
      </AppProvider>
    </ErrorBoundary>
  </StrictMode>
);

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  // The hot module reloading API is not available in production.
  createRoot(elem).render(app);
}
